import type { SubscriptionTier } from "@/types/projects";
import {
  createBookExportCapabilityMatrix,
  createDefaultBookExportConfig,
  isBookExportEndMatterMessageSource,
  isBookExportFrontMatterMessageSource,
  isBookExportMode,
  isBookExportPageSize,
  isBookExportPreset,
  isBookExportTarget,
  isBookExportTierAllowed,
  type BookExportCapabilityMatrix,
  type BookExportConfig,
  type BookExportTier,
} from "./export-contract";

function coerceConfig(
  config: Partial<BookExportConfig> | Record<string, unknown> | null | undefined,
  defaults: BookExportConfig,
): BookExportConfig {
  const value = config ?? {};

  return createDefaultBookExportConfig({
    ...defaults,
    endMatterMessageSource: isBookExportEndMatterMessageSource(value.endMatterMessageSource)
      ? value.endMatterMessageSource
      : defaults.endMatterMessageSource,
    exportMode: isBookExportMode(value.exportMode) ? value.exportMode : defaults.exportMode,
    frontMatterMessageSource: isBookExportFrontMatterMessageSource(
      value.frontMatterMessageSource,
    )
      ? value.frontMatterMessageSource
      : defaults.frontMatterMessageSource,
    includeCharacterAppendix: value.includeCharacterAppendix === true,
    includeCover: value.includeCover !== false,
    includeIllustrations: value.includeIllustrations === true,
    includeLoreAppendix: value.includeLoreAppendix === true,
    includeTimelineAppendix: value.includeTimelineAppendix === true,
    includeTitlePage: value.includeTitlePage !== false,
    includeToc: value.includeToc !== false,
    mirroredMargins: value.mirroredMargins === true,
    pageSize: isBookExportPageSize(value.pageSize) ? value.pageSize : defaults.pageSize,
    preset: isBookExportPreset(value.preset) ? value.preset : defaults.preset,
    printMode: value.printMode === true,
    selectedSideStoryIds: Array.isArray(value.selectedSideStoryIds)
      ? value.selectedSideStoryIds.filter((entry): entry is string => typeof entry === "string")
      : defaults.selectedSideStoryIds,
    showPageNumbers: value.showPageNumbers !== false,
    startChaptersOnRightPage: value.startChaptersOnRightPage === true,
    target: isBookExportTarget(value.target) ? value.target : defaults.target,
  });
}

export function getBookExportCapabilitiesForTier(
  tier: SubscriptionTier | BookExportTier,
): BookExportCapabilityMatrix {
  return createBookExportCapabilityMatrix(tier);
}

export function normalizeBookExportConfigForSource(input: {
  availableSideStoryIds?: string[];
  capabilities: BookExportCapabilityMatrix;
  config: Partial<BookExportConfig> | Record<string, unknown> | null | undefined;
  hasMainStory: boolean;
}): BookExportConfig {
  const defaults = input.capabilities.defaultConfig;
  const availableSideStoryIds = new Set(input.availableSideStoryIds ?? []);
  const normalized = coerceConfig(input.config, defaults);

  if (!input.capabilities.allowedTargets.includes(normalized.target)) {
    normalized.target = defaults.target;
  }

  if (!input.capabilities.allowedModes.includes(normalized.exportMode)) {
    normalized.exportMode = defaults.exportMode;
  }

  if (!input.capabilities.allowedPresets.includes(normalized.preset)) {
    normalized.preset = defaults.preset;
  }

  if (!input.capabilities.allowedPageSizes.includes(normalized.pageSize)) {
    normalized.pageSize = defaults.pageSize;
  }

  if (
    !isBookExportTierAllowed(
      input.capabilities.tier,
      input.capabilities.frontMatterSourceRequirements[normalized.frontMatterMessageSource],
    )
  ) {
    normalized.frontMatterMessageSource = defaults.frontMatterMessageSource;
  }

  if (
    !isBookExportTierAllowed(
      input.capabilities.tier,
      input.capabilities.endMatterSourceRequirements[normalized.endMatterMessageSource],
    )
  ) {
    normalized.endMatterMessageSource = defaults.endMatterMessageSource;
  }

  if (!input.capabilities.allowIllustrations) {
    normalized.includeIllustrations = false;
  }

  if (!input.capabilities.allowPrintMode) {
    normalized.printMode = false;
  }

  if (!normalized.printMode) {
    normalized.mirroredMargins = false;
    normalized.startChaptersOnRightPage = false;
  } else {
    if (!input.capabilities.allowMirroredMargins) {
      normalized.mirroredMargins = false;
    }

    if (!input.capabilities.allowRightPageStarts) {
      normalized.startChaptersOnRightPage = false;
    }
  }

  if (!input.capabilities.allowAppendices) {
    normalized.includeCharacterAppendix = false;
    normalized.includeLoreAppendix = false;
    normalized.includeTimelineAppendix = false;
  }

  normalized.selectedSideStoryIds = normalized.selectedSideStoryIds.filter(
    (storyId) => availableSideStoryIds.size === 0 || availableSideStoryIds.has(storyId),
  );

  if (!input.hasMainStory && normalized.exportMode !== "side_stories_only") {
    normalized.exportMode = input.capabilities.allowedModes.includes("side_stories_only")
      ? "side_stories_only"
      : defaults.exportMode;
  }

  if (normalized.exportMode === "story_only") {
    normalized.selectedSideStoryIds = [];
    normalized.includeCharacterAppendix = false;
    normalized.includeLoreAppendix = false;
    normalized.includeTimelineAppendix = false;
  }

  if (normalized.exportMode === "side_stories_only") {
    normalized.includeCharacterAppendix = false;
    normalized.includeLoreAppendix = false;
    normalized.includeTimelineAppendix = false;
  }

  if (normalized.exportMode !== "full_compendium") {
    normalized.includeCharacterAppendix = false;
    normalized.includeLoreAppendix = false;
    normalized.includeTimelineAppendix = false;
  }

  return normalized;
}
